import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "../../../../../../../../../shared/Box/Box";
import { Table as SharedTable } from "../../../../../../../../../shared/Table/Table";
import { useAppTranslation } from "../../../../../../../../../i18n/useAppTranslation";
import { Skeleton } from "../../../../../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../../../../../shared/Empty/Empty";
import { ContentLoader } from "../../../../../../../../../boxes/ContentLoader/ContentLoader";
import { LoadMoreObserver } from "../../../../../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import classes from "./Table.module.scss";

export function Table({ reports }) {
  const { tCommon } = useAppTranslation.Common();
  const { tBi } = useAppTranslation.BusinessInsights();

  const [expandedRows, setExpandedRows] = useState([]);

  const isEmpty = reports.value.length === 0 && !reports.isLoading;
  const isVisible = !reports.isLoading && !isEmpty;

  // shape rows: when items is empty, show old behavior; when items exist, show expandable children
  const shaped = useMemo(() => {
    return reports.value.map((r, idx) => {
      const hasItems = Array.isArray(r.items) && r.items.length > 0;
      const idPart = r.patientId != null ? r.patientId : r.patientName || idx;
      const baseRow = {
        key: `${idPart}-${idx}`,
        date: hasItems ? "" : r.date,
        patient: r.patientName || "-",
        clinicName: r.clinicName || "-",
        productName: r.productName || "-",
        units: r.unitsCount,
        dollarValue: r.dollarValue,
        balanceUnits: r.balanceUnitsCount,
        walletBalanceDollarValue: r.walletBalanceDollarValue,
        balanceServiceValue: r.balanceServiceValue,
        index: idx,
      };

      const children = hasItems
        ? r.items.map((it, iidx) => ({
            key: `${idPart}-child-${iidx}`,
            date: it.date,
            patient: "",
            clinicName: it.clinic_name || baseRow.clinicName,
            productName: it.product_name || baseRow.productName,
            units: it.units,
            dollarValue: it.dollar_value,
            balanceUnits: it.balance_units,
            walletBalanceDollarValue: it.wallet_balance_dollar_value,
            balanceServiceValue: it.balance_service_value,
          }))
        : undefined; // undefined to hide expand icon when no items

      return { ...baseRow, children };
    });
  }, [reports.value]);

  const data = shaped;

  const toggleRow = (row) => {
    if (!row.children || row.children.length === 0) {
      return; // do not toggle for non-expandable rows
    }
    const idx = row.index;
    setExpandedRows((prev) =>
      prev.includes(idx) ? prev.filter((r) => r !== idx) : [...prev, idx],
    );
  };

  return (
    <Box overflowXScroll>
      {reports.isLoading && !reports.isSoftLoading && (
        <Skeleton count={15} height={39.06} borderRadius="0px" />
      )}
      {isEmpty && <Empty position="center" />}
      {isVisible && (
        <ContentLoader isLoading={reports.isSoftLoading}>
          <SharedTable
            bgColor="white"
            data={data}
            expandedRows={expandedRows}
            onRowClick={(row) => toggleRow(row)}
            cols={[
              { data: tCommon("label.expirationDate"), accessor: "date" },
              {
                data: tCommon("label.patient"),
                accessor: "patient",
                className: classes.patientName,
              },
              {
                data: tBi("shortTermLiabilityReport.table.clinicName"),
                accessor: "clinicName",
                className: classes.clinicName,
              },
              {
                data: tCommon("label.productName"),
                accessor: "productName",
                className: classes.productName,
              },
              { data: tCommon("label.units"), accessor: "units" },
              { data: tCommon("label.dollarValue"), accessor: "dollarValue" },
              { data: tCommon("label.balanceUnits"), accessor: "balanceUnits" },
              {
                data: tBi(
                  "shortTermLiabilityReport.table.walletBalanceDollarValue",
                ),
                accessor: "walletBalanceDollarValue",
              },
              {
                data: tBi("shortTermLiabilityReport.table.balanceServiceValue"),
                accessor: "balanceServiceValue",
              },
            ]}
          />
          {reports.hasMore && (
            <LoadMoreObserver
              noPaddingBottom
              loadMore={reports.fetchMore}
              isLoading={reports.isNextFetching}
            />
          )}
        </ContentLoader>
      )}
    </Box>
  );
}

Table.propTypes = {
  reports: PropTypes.object.isRequired,
};
